import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from "@/hooks/use-toast";
import AttendanceTable from './AttendanceTable';

interface AttendanceRecord {
  id: string;
  attended: boolean;
  student: {
    first_name: string;
    last_name: string;
  };
}

interface SessionAttendanceActionsProps {
  sessionId: string;
  sessionType: 'training' | 'promotion';
  userRole: string | undefined;
  showAttendance: boolean;
}

const SessionAttendanceActions: React.FC<SessionAttendanceActionsProps> = ({
  sessionId,
  sessionType,
  userRole,
  showAttendance
}) => {
  useUiLanguage();
  const { profile } = useAuth();
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userResponse, setUserResponse] = useState<boolean | null>(null);

  const fetchAttendanceRecords = async () => {
    if (sessionType !== 'training' || (userRole !== 'admin' && userRole !== 'super_user')) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('session_attendance')
        .select(`
          id,
          attended,
          student:profiles!session_attendance_student_id_fkey(
            first_name,
            last_name
          )
        `)
        .eq('session_id', sessionId);

      if (error) throw error;
      setAttendanceRecords(data || []);
    } catch (error) {
      console.error('Error fetching attendance records:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserResponse = async () => {
    if (!profile || sessionType !== 'training') return;

    try {
      const { data, error } = await supabase
        .from('session_attendance')
        .select('attended')
        .eq('session_id', sessionId)
        .eq('student_id', profile.id)
        .maybeSingle();

      if (error) throw error;
      setUserResponse(data?.attended || null);
    } catch (error) {
      console.error('Error fetching user response:', error);
    }
  };

  useEffect(() => {
    if (showAttendance && sessionType === 'training' && (userRole === 'admin' || userRole === 'super_user')) {
      fetchAttendanceRecords();
    }
  }, [showAttendance, sessionId, sessionType, userRole]);

  useEffect(() => {
    if (userRole === 'student') {
      fetchUserResponse();
    }
  }, [profile, sessionId, userRole]);

  if (!showAttendance || (userRole !== 'admin' && userRole !== 'super_user') || sessionType !== 'training') {
    return null;
  }

  return (
    <div className="mt-4 pt-4 border-t">
      <h5 className="font-medium text-gray-900 mb-3">{uiText("Student Confirmations")}</h5>
      <AttendanceTable
        attendanceRecords={attendanceRecords}
        isLoading={isLoading}
      />
    </div>
  );
};

export default SessionAttendanceActions;
